import { getConsent, onConsentChange } from './consent';

declare global {
  interface Window {
    fbq: ((...args: unknown[]) => void) & { callMethod?: (...args: unknown[]) => void; queue?: unknown[]; loaded?: boolean; version?: string; push?: (...args: unknown[]) => void };
    _fbq: typeof window.fbq;
  }
}

let fbPixelInitialized = false;
let pendingPixelId: string | null = null;

/**
 * Initialize Facebook Pixel. Uses strict gating — the script is only injected
 * when marketing consent is granted. If consent is not yet granted, queues the
 * pixel ID and loads when consent is given.
 */
export function initFBPixel(pixelId: string): void {
  if (fbPixelInitialized || !pixelId) return;

  const consent = getConsent();
  if (consent?.marketing) {
    injectPixelScript(pixelId);
  } else {
    // Queue for injection when marketing consent is granted
    pendingPixelId = pixelId;
    onConsentChange((prefs) => {
      if (prefs.marketing && pendingPixelId && !fbPixelInitialized) {
        injectPixelScript(pendingPixelId);
        pendingPixelId = null;
      }
    });
  }
}

/**
 * Track a Facebook Pixel event.
 */
export function trackFBEvent(eventName: string, params?: Record<string, unknown>): void {
  if (!fbPixelInitialized || !window.fbq) return;
  if (params) {
    window.fbq('track', eventName, params);
  } else {
    window.fbq('track', eventName);
  }
}

function injectPixelScript(pixelId: string): void {
  // FB Pixel base code
  const n = (window.fbq = function (...args: unknown[]) {
    n.callMethod ? n.callMethod.apply(n, args) : n.queue!.push(args);
  } as typeof window.fbq);
  if (!window._fbq) window._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  script.setAttribute('data-analytics', 'fb-pixel');
  document.head.appendChild(script);

  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');

  fbPixelInitialized = true;
}
